/**
 * Thin Laravel Echo wrapper. The Echo + Pusher imports are loaded lazily so
 * the rest of the app keeps building cleanly when the deps aren't installed
 * (e.g. during initial backend setup).
 *
 * Connects to a Reverb server using `VITE_REVERB_*` env vars; if any are
 * missing, exposes a no-op subscriber so consumers never need to branch.
 */

import { api } from "./api";

type EchoChannel = {
  listen: (event: string, cb: (payload: unknown) => void) => EchoChannel;
};
type EchoLike = {
  private: (channel: string) => EchoChannel;
  leave: (channel: string) => void;
  disconnect?: () => void;
};

let echoPromise: Promise<EchoLike | null> | null = null;

function envFlag(): boolean {
  // Only the APP_KEY must be set at build time; host is detected from
  // window.location at runtime (see buildEcho below) so the same bundle
  // serves dev (control.localhost) and prod (control.jowcey.com).
  return !!import.meta.env.VITE_REVERB_APP_KEY;
}

async function buildEcho(): Promise<EchoLike | null> {
  if (!envFlag()) return null;
  try {
    // Dynamic import — keeps initial bundle small AND avoids a hard build
    // failure if the package isn't installed in development.
    const [{ default: Echo }, Pusher] = await Promise.all([
      import(/* @vite-ignore */ "laravel-echo") as Promise<{ default: new (cfg: Record<string, unknown>) => unknown }>,
      import(/* @vite-ignore */ "pusher-js") as Promise<{ default?: unknown }>,
    ]);
    // Echo expects a global Pusher.
    (window as unknown as { Pusher: unknown }).Pusher = Pusher.default ?? Pusher;
    // Runtime-adaptive host + port + TLS so one frontend build serves
    // both `https://control.jowcey.com` (production) and
    // `http://control.localhost` (dev) without rebaking VITE_* args
    // per environment. Build-time VITE_REVERB_HOST still wins if set,
    // for cases where Reverb runs on a different hostname (e.g. a
    // dedicated WebSocket subdomain).
    const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
    const wsHost  = (import.meta.env.VITE_REVERB_HOST as string | undefined)
      || (typeof window !== "undefined" ? window.location.hostname : "");
    const wsPort  = Number(import.meta.env.VITE_REVERB_PORT ?? (isHttps ? 443 : 80));
    const echo = new Echo({
      broadcaster: "reverb",
      key:    import.meta.env.VITE_REVERB_APP_KEY,
      wsHost,
      wsPort,
      wssPort: wsPort,
      forceTLS: isHttps,
      enabledTransports: isHttps ? ["wss"] : ["ws"],
      // Through api() so the cookie session, XSRF token and tenant header
      // go with it; the old authEndpoint 404'd and sent no CSRF (FE-17).
      authorizer: (channel: { name: string }) => ({
        authorize: (socketId: string, callback: (error: Error | null, data?: unknown) => void) => {
          api("/broadcasting/auth", { method: "POST", body: { socket_id: socketId, channel_name: channel.name } })
            .then((data) => callback(null, data))
            .catch((e) => callback(e instanceof Error ? e : new Error(String(e))));
        },
      }),
    });
    return echo as unknown as EchoLike;
  } catch (e) {
    if (typeof console !== "undefined") {
      console.warn("Echo not available:", (e as Error).message);
    }
    return null;
  }
}

export function getEcho(): Promise<EchoLike | null> {
  if (!echoPromise) echoPromise = buildEcho();
  return echoPromise;
}

/**
 * Subscribe to a private channel. Returns an unsubscribe function. If Echo
 * isn't available, returns a no-op so callers never have to branch.
 */
export async function subscribePrivate(
  channelName: string,
  events: Record<string, (payload: unknown) => void>
): Promise<() => void> {
  const echo = await getEcho();
  if (!echo) return () => {};
  const channel = echo.private(channelName);
  Object.entries(events).forEach(([event, cb]) => channel.listen(event, cb));
  return () => {
    try { echo.leave(channelName); } catch (_) { /* noop */ }
  };
}

/**
 * Phase A.1 — subscribe to the active tenant's "list changed" bus and
 * fan onto the local `data:changed` event so every active `useApi`
 * hook refetches.
 *
 * Returns an unsubscribe function. The Shell mounts this once; the
 * fallback path (no Reverb) is the existing per-mutation bus emit
 * inside the api client, so peer updates simply don't propagate
 * cross-tab.
 */
import { bus } from "./bus";

export async function subscribeTenantBus(tenantId: number): Promise<() => void> {
  return subscribePrivate(`tenant.${tenantId}`, {
    ".changed": () => bus.emit("data:changed"),
    // Reverb prefixes events with "." when broadcastAs() is set; keep
    // the un-prefixed alias too in case the Echo layer is normalised.
    "changed":  () => bus.emit("data:changed"),
  });
}

/**
 * Phase A.1 — comment-thread channel. CommentThread mounts this with
 * its parent record; on any event the thread refetches.
 */
export async function subscribeCommentChannel(
  parentType: "work_order" | "nc" | "risk_assessment",
  parentId: number,
  onChange: () => void,
): Promise<() => void> {
  return subscribePrivate(`comment.${parentType}.${parentId}`, {
    ".changed": onChange,
    "changed":  onChange,
  });
}
